import { Configuration, FrontendApi, IdentityApi } from '@ory/client';
import { oryConfig } from './ory-config';
// Helper to check if we're in development mode (works with Vite)
const isDevelopment = () => {
    // Check Vite environment
    if (typeof window !== 'undefined' && window.import?.meta?.env?.DEV) {
        return true;
    }
    // Fallback to process.env for Node.js environments
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        return true;
    }
    return false;
};
export class OryService {
    frontendApi;
    identityApi;
    constructor() {
        const config = new Configuration({
            basePath: oryConfig.publicUrl,
            baseOptions: {
                withCredentials: true,
            },
        });
        const adminConfig = new Configuration({
            basePath: oryConfig.adminUrl,
            accessToken: oryConfig.adminApiKey,
        });
        this.frontendApi = new FrontendApi(config);
        this.identityApi = new IdentityApi(adminConfig);
    }
    // Settings Flow Operations
    async createSettingsFlow(returnTo) {
        try {
            const response = await this.frontendApi.createBrowserSettingsFlow({
                returnTo
            });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to create settings flow', error);
            throw error;
        }
    }
    async getSettingsFlow(flowId) {
        try {
            const response = await this.frontendApi.getSettingsFlow({ id: flowId });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to get settings flow', error);
            throw error;
        }
    }
    async updateSettingsFlow(flowId, body) {
        try {
            const response = await this.frontendApi.updateSettingsFlow({
                flow: flowId,
                updateSettingsFlowBody: body
            });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to update settings flow', error);
            throw error;
        }
    }
    // Login Flow Operations
    async createLoginFlow(returnTo) {
        try {
            const response = await this.frontendApi.createBrowserLoginFlow({
                returnTo,
                aal: 'aal1'
            });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to create login flow', error);
            throw error;
        }
    }
    async getLoginFlow(flowId) {
        try {
            const response = await this.frontendApi.getLoginFlow({ id: flowId });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to get login flow', error);
            throw error;
        }
    }
    async updateLoginFlow(flowId, body) {
        try {
            const response = await this.frontendApi.updateLoginFlow({
                flow: flowId,
                updateLoginFlowBody: body
            });
            return response.data;
        }
        catch (error) {
            // Don't log 400 errors that contain flow data (these are expected flow updates)
            const is400WithFlowData = error.response?.status === 400 && error.response?.data?.ui;
            if (!is400WithFlowData) {
                this.logError('Failed to update login flow', error);
            }
            throw error;
        }
    }
    // Recovery Flow Operations (for invitations/magic links)
    async createRecoveryFlow(returnTo) {
        try {
            const response = await this.frontendApi.createBrowserRecoveryFlow({
                returnTo
            });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to create recovery flow', error);
            throw error;
        }
    }
    async getRecoveryFlow(flowId) {
        try {
            const response = await this.frontendApi.getRecoveryFlow({ id: flowId });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to get recovery flow', error);
            throw error;
        }
    }
    async submitRecoveryFlow(flowId, body) {
        try {
            const response = await this.frontendApi.updateRecoveryFlow({
                flow: flowId,
                updateRecoveryFlowBody: body
            });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to submit recovery flow', error);
            throw error;
        }
    }
    // Verification Flow Methods
    async createVerificationFlow(returnTo) {
        const { data } = await this.frontendApi.createBrowserVerificationFlow({
            returnTo,
        });
        return data;
    }
    async getVerificationFlow(flowId) {
        const { data } = await this.frontendApi.getVerificationFlow({ id: flowId });
        return data;
    }
    // Session Methods
    async getCurrentSession() {
        try {
            const { data } = await this.frontendApi.toSession();
            return data;
        }
        catch (error) {
            return null;
        }
    }
    async logout(returnTo) {
        try {
            const { data } = await this.frontendApi.createBrowserLogoutFlow({
                returnTo,
            });
            // Return the logout URL for the browser to redirect to
            return data.logout_url;
        }
        catch (error) {
            this.logError('Failed to logout', error);
            throw error;
        }
    }
    // Session Management Methods
    async listMySessions(page = 1, perPage = 10) {
        try {
            const { data } = await this.frontendApi.listMySessions({
                page,
                perPage,
            });
            return data;
        }
        catch (error) {
            this.logError('Failed to list sessions', error);
            throw error;
        }
    }
    async disableMySession(sessionId) {
        try {
            await this.frontendApi.disableMySession({
                id: sessionId,
            });
        }
        catch (error) {
            this.logError('Failed to disable session', error);
            throw error;
        }
    }
    async disableMyOtherSessions() {
        try {
            const { data } = await this.frontendApi.disableMyOtherSessions();
            return data.count || 0;
        }
        catch (error) {
            this.logError('Failed to disable other sessions', error);
            throw error;
        }
    }
    // Identity Methods (Admin)
    async createIdentity(email, traits, verifiedEmail = false) {
        const { data } = await this.identityApi.createIdentity({
            createIdentityBody: {
                schema_id: 'default',
                traits: {
                    email,
                    ...traits,
                },
                verifiable_addresses: verifiedEmail
                    ? [
                        {
                            value: email,
                            verified: true,
                            via: 'email',
                            status: 'completed',
                        },
                    ]
                    : undefined,
            },
        });
        return data;
    }
    async getIdentity(identityId) {
        const { data } = await this.identityApi.getIdentity({ id: identityId });
        return data;
    }
    async updateIdentity(identityId, traits) {
        const { data } = await this.identityApi.updateIdentity({
            id: identityId,
            updateIdentityBody: {
                schema_id: 'default',
                traits,
                state: 'active', // Add the required state property
            },
        });
        return data;
    }
    /**
     * Detect available authentication methods from login flow
     * This examines the UI nodes to determine which methods are available
     */
    async detectAvailableMethodsFromFlow(loginFlow) {
        const methods = [];
        // Email OTP is always available in passwordless setup
        methods.push('code');
        // Check UI nodes for available methods
        // For passkeys, we need to be more specific about what indicates actual enrollment
        let hasPasskeyNodes = false;
        let hasPasskeyChallenge = false;
        loginFlow.ui.nodes.forEach(node => {
            if (node.group === 'passkey') {
                hasPasskeyNodes = true;
                const attrs = node.attributes;
                // A passkey_challenge with actual value indicates enrolled passkeys
                if (attrs.name === 'passkey_challenge' && attrs.value) {
                    hasPasskeyChallenge = true;
                }
            }
        });
        // Only add passkey if we have nodes AND a challenge (indicating enrollment)
        // In identifier-first flow, Kratos includes passkey nodes even without enrollment
        // but only provides challenge data when user has passkeys
        if (hasPasskeyNodes && hasPasskeyChallenge) {
            methods.push('passkey');
        }
        return methods;
    }
    // Utility Methods
    getCSRFToken(flow) {
        const csrfNode = flow.ui.nodes.find((node) => node.attributes &&
            'name' in node.attributes &&
            node.attributes.name === 'csrf_token');
        if (csrfNode && csrfNode.attributes && 'value' in csrfNode.attributes) {
            return csrfNode.attributes.value;
        }
        return undefined;
    }
    filterNodesByGroup(flow, groups) {
        return flow.ui.nodes.filter((node) => groups.includes(node.group));
    }
    getNodeValue(flow, nodeName) {
        const node = flow.ui.nodes.find((n) => n.attributes && 'name' in n.attributes && n.attributes.name === nodeName);
        if (node && node.attributes && 'value' in node.attributes) {
            return node.attributes.value;
        }
        return undefined;
    }
    /**
     * Fetch user-facing error details from Ory
     * Used when Ory redirects to the error UI with an error ID
     * @see https://www.ory.com/docs/kratos/self-service/flows/user-facing-errors
     */
    async getFlowError(errorId) {
        try {
            const response = await this.frontendApi.getFlowError({ id: errorId });
            return response.data;
        }
        catch (error) {
            this.logError('Failed to fetch flow error', error);
            return null;
        }
    }
    /**
     * Log error conditionally based on environment
     */
    logError(context, error) {
        if (isDevelopment()) {
            console.error(`[OryService] ${context}:`, error);
        }
    }
}
// Export singleton instance
export const oryService = new OryService();
